import * as React from 'react';
import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {CookiesProvider, useCookies} from 'react-cookie';
import {Warehouse} from '../../shared/CommonInterfaces';
import LoadsService from '../../shared/services/LoadsService';
import FlexeContext from '../../contexts/FlexeContext';
import RelativeDatePicker, {
  getDefaultCreatedAfter,
  RelativeDateOption
} from '../../shared/relative-date-picker/RelativeDatePicker';
import {LoadsTable} from './LoadsTable';
import {UpsertLoadModal} from './create-load-modal/UpsertLoadModal';
import {LoadSummary} from './LoadInterfaces';

interface LoadsIndexProps {
  authenticityToken: string;
  activeWarehouses: Warehouse[];
  inactiveWarehouses: Warehouse[];
  selectedWarehouse: Warehouse;
  parcelTrailerLoadingEnabled: boolean; // TODO remove when loads UI is fully released
  freightTrailerLoadingEnabled: boolean; // TODO remove when loads UI is fully released
  isConfiguredForSortProcesses: boolean;
}

const LoadsIndex: React.FC<LoadsIndexProps> = (props) => {
  const {authenticityToken} = useContext(FlexeContext);
  const loadsService = new LoadsService(authenticityToken);
  const [chosenWarehouse, setChosenWarehouse] = useState<Warehouse>(props.selectedWarehouse);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [rows, setRows] = useState<LoadSummary[]>([]);
  const continuationToken = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>(null);
  const [createdAfter, setCreatedAfter] = useState<Date>(getDefaultCreatedAfter(RelativeDateOption.lastSevenDays));

  const [cookies] = useCookies(['selected_warehouse_id']);

  const selectedWarehouse = useMemo(() => {
    if (cookies.selected_warehouse_id) {
      return [...props.activeWarehouses, ...props.inactiveWarehouses].find(
        (warehouse: Warehouse) => warehouse.id === parseInt(cookies.selected_warehouse_id, 10)
      );
    }

    return chosenWarehouse;
  }, [cookies, chosenWarehouse, props.activeWarehouses]);

  useEffect(() => {
    fetchLoads();
  }, [selectedWarehouse, createdAfter]);

  const fetchLoads = async () => {
    setError(null);
    setIsLoading(true);

    let allLoads: LoadSummary[] = [];
    do {
      const response = await loadsService.getLoads(selectedWarehouse.id, continuationToken.current, createdAfter);
      if (response.errors.length > 0) {
        setError(`There was an error while retrieving loads: ${response.errors[0].detail}`);
        setIsLoading(false);
        return;
      } else {
        allLoads = [...allLoads, ...response.data.loads];
        continuationToken.current = response.data.continuationToken;
      }
    } while (continuationToken.current);

    setRows(allLoads);
    setIsLoading(false);
  };

  const toggleCreateLoadModal = () => {
    setShowCreateModal(!showCreateModal);
  };

  return (
    <div className="loads-index-page container-fluid">
      <div className="loads-header">
        <h1>Loads</h1>
        {props.freightTrailerLoadingEnabled && (
          <button className="btn btn-primary create-load-button" onClick={toggleCreateLoadModal}>
            <i className="fa fa-plus" />
            New Load
          </button>
        )}
      </div>
      <div>
        {error != null && (
          <div className="alert alert-danger" role="alert" key={`error:${1}`}>
            {error}
          </div>
        )}
      </div>
      <div className="loads-created-after">
        <RelativeDatePicker
          onDateSelected={setCreatedAfter}
          prefixText={'Created: '}
          allowedRelativeDates={[
            RelativeDateOption.today,
            RelativeDateOption.lastSevenDays,
            RelativeDateOption.lastThirtyDays,
            RelativeDateOption.lastNinetyDays,
            RelativeDateOption.anytime
          ]}
          initialRelativeDate={RelativeDateOption.lastSevenDays}
        />
      </div>
      <LoadsTable data={rows} isLoading={isLoading} warehouse={selectedWarehouse} />
      <UpsertLoadModal
        isOpen={showCreateModal}
        toggleModal={toggleCreateLoadModal}
        cancelUpsert={toggleCreateLoadModal}
        warehouseId={selectedWarehouse.id}
        loadState={null}
        onCreateLoad={fetchLoads}
      />
    </div>
  );
};

export default LoadsIndex;
